import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X } from 'lucide-react';
import useAuth from '../hooks/useAuth';
import notif from '../utils/notificationService';

// Small bottom-right banner that nudges the user to enable browser
// notifications + sounds. Persists "user dismissed" state in localStorage
// so we don't badger them on every page load.
//
// Auto-hides:
//   - permission is 'granted' or 'denied' (final state)
//   - user clicked Dismiss in a prior session
//   - user isn't logged in
const DISMISSED_KEY = 'notif.prompt.dismissed.v1';

const NotificationPrompt = () => {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [muted, setMutedState] = useState(notif.isMuted());

    useEffect(() => {
        if (!user) { setVisible(false); return; }
        if (typeof Notification === 'undefined') return;
        const dismissed = localStorage.getItem(DISMISSED_KEY) === '1';
        // Show only on "default" — we haven't asked yet AND user hasn't dismissed
        if (Notification.permission === 'default' && !dismissed) {
            // Slight delay so it doesn't pop instantly on page load
            const t = setTimeout(() => setVisible(true), 1500);
            return () => clearTimeout(t);
        }
    }, [user]);

    const allow = async () => {
        const result = await notif.requestPermission();
        if (result === 'granted') {
            notif.playSound('default'); // confirmation chirp
        }
        setVisible(false);
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* ignore */ }
        setVisible(false);
    };

    const toggleMute = () => {
        const next = !muted;
        notif.setMuted(next);
        setMutedState(next);
        if (!next) notif.playSound('default'); // unmuting → quick confirm chirp
    };

    return (
        <>
            {/* Persistent mute toggle — always visible bottom-left so users
                can silence the portal anytime (e.g. during a presentation). */}
            {user && (
                <button
                    onClick={toggleMute}
                    title={muted ? 'Sounds are muted — click to unmute' : 'Mute notification sounds'}
                    className="fixed bottom-6 left-6 z-[58] w-11 h-11 rounded-full bg-white border border-[#8a6144]/20 shadow-lg flex items-center justify-center text-[#8a6144] hover:bg-[#fff5e6] hover:scale-110 transition-all"
                >
                    {muted ? <BellOff className="w-5 h-5 text-rose-500" /> : <Bell className="w-5 h-5" />}
                </button>
            )}

            {/* One-time permission prompt */}
            <AnimatePresence>
                {visible && (
                    <motion.div
                        initial={{ opacity: 0, y: 60, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 60, scale: 0.95 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bottom-24 left-6 z-[58] max-w-sm bg-white rounded-2xl shadow-2xl border border-[#8a6144]/20 p-4 flex items-start gap-3"
                    >
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#8a6144] to-[#6b4d36] text-white flex items-center justify-center shadow">
                            <Bell className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="font-extrabold text-sm text-[#433020] mb-0.5">
                                Stay in the loop
                            </h4>
                            <p className="text-xs text-[#6b4d36] leading-relaxed mb-3">
                                Get instant alerts for new tasks, meetings, and chat messages — even when this tab is in the background.
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={allow}
                                    className="flex-1 px-3 py-1.5 bg-gradient-to-br from-[#8a6144] to-[#6b4d36] hover:from-[#6b4d36] hover:to-[#433020] text-white rounded-lg text-xs font-bold shadow"
                                >
                                    Enable notifications
                                </button>
                                <button
                                    onClick={dismiss}
                                    className="px-3 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700"
                                >
                                    Not now
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={dismiss}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default NotificationPrompt;
